import React from 'react';
import {
  Button as MuiButton,
  CircularProgress,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { designTokens } from '../../../design-system/tokens';

/**
 * LinerButton Props
 */
export interface LinerButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  /** 로딩 상태 */
  loading?: boolean;
  /** 버튼 내용 */
  children: React.ReactNode;
  /** 테스트 ID */
  testId?: string;
}

/**
 * Dream Logistics Liner Button 컴포넌트
 *
 * Dream Dark (#003F5F) 색상을 사용하는 아웃라인 스타일 버튼
 *
 * 스펙:
 * - 최소 너비: 80px
 * - 높이: 30px
 * - 패딩: 0 27px
 * - 테두리 색상: #003F5F (Dream Dark)
 * - 텍스트 색상: #003F5F (Dream Dark)
 * - Border Radius: 4px
 *
 * @component
 * @example
 * // 기본 사용
 * <LinerButton onClick={handleClick}>
 *   취소
 * </LinerButton>
 *
 * @example
 * // 로딩 상태
 * <LinerButton loading={isSubmitting} onClick={handleSubmit}>
 *   제출
 * </LinerButton>
 *
 * @example
 * // 아이콘과 함께 사용
 * <LinerButton startIcon={<CloseIcon />} onClick={handleClose}>
 *   닫기
 * </LinerButton>
 */
export const LinerButton: React.FC<LinerButtonProps> = ({
  children,
  loading = false,
  disabled = false,
  startIcon,
  endIcon,
  onClick,
  type = 'button',
  className,
  testId,
  sx,
  ...props
}) => {
  const isDisabled = disabled || loading;

  return (
    <MuiButton
      variant="outlined"
      disabled={isDisabled}
      onClick={onClick}
      type={type}
      className={className}
      data-testid={testId}
      startIcon={loading ? undefined : startIcon}
      endIcon={loading ? undefined : endIcon}
      sx={{
        // Layout
        display: 'flex',
        minWidth: '80px',
        height: '30px',
        padding: '0 27px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',

        // Style
        borderRadius: '4px',
        border: `1px solid ${designTokens.colors.brand.dreamDark}`,
        backgroundColor: 'transparent',
        color: designTokens.colors.brand.dreamDark,

        // Typography
        fontSize: designTokens.typography.fontSize.sm,
        fontWeight: designTokens.typography.fontWeight.medium,
        textTransform: 'none',

        // Interaction
        boxShadow: 'none',
        transition: `all ${designTokens.animations.duration.normal} ${designTokens.animations.easing.easeInOut}`,

        '&:hover': {
          border: `1px solid #005A7F`, // dreamDark.light
          backgroundColor: 'rgba(0, 63, 95, 0.04)',
          color: '#005A7F',
          boxShadow: designTokens.shadows.sm,
          transform: 'translateY(-1px)',
        },

        '&:active': {
          border: `1px solid #002940`, // dreamDark.dark
          backgroundColor: 'rgba(0, 63, 95, 0.08)',
          color: '#002940',
          transform: 'translateY(0)',
        },

        '&.Mui-disabled': {
          border: '1px solid #BDBDBD',
          color: '#BDBDBD',
          opacity: 0.6,
        },

        // 사용자 정의 sx 속성 병합
        ...sx,
      }}
      {...props}
    >
      {loading && <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />}
      {children}
    </MuiButton>
  );
};

export default LinerButton;
